import { differenceInDays, isThisMonth, parseISO } from 'date-fns';
import { createSelector } from 'reselect';

import {
  getTotalPrice,
  getDirectTotalPrice,
} from '../helpers/data-parsing/gridEntryReader';
import { gridEntryPassesFilters } from '../helpers/dateSelectionValidators';

import {
  getCheapestCombinations,
  getMonthData,
  getPreferDirects,
  getSelectedInboundMonth,
  getSelectedOutboundMonth,
  getTripDuration,
  isReturn,
} from '.';

import type { DateSelectionState, MonthDataGridElement, State } from './types';

export type CheapestCombination = {
  outboundIndex: number;
  inboundIndex: number;
  price: number;
  isDirect: boolean;
};

const getMonthDiff = (
  outboundMonth: Date,
  inboundMonth: Date | null,
): number =>
  inboundMonth
    ? (inboundMonth.getFullYear() - outboundMonth.getFullYear()) * 12 +
      (inboundMonth.getMonth() - outboundMonth.getMonth())
    : 0;

// Calculates the proportion of outbound/inbound combinations that have a total price.
// Callers typically treat a value >= 0.75 as "sufficiently complete" to enable
// cheapest-date highlighting; below this threshold, they fall back to standard
// calendar behaviour to avoid surfacing recommendations based on sparse data.
export const calculateDataCompleteness = createSelector(
  getMonthData,
  getSelectedOutboundMonth,
  getSelectedInboundMonth,
  (
    grid: MonthDataGridElement[][],
    outboundMonth: Date,
    inboundMonth: Date | null,
  ): number => {
    if (!grid || grid.length === 0) return 0;

    let total = 0;
    let withPrices = 0;

    const monthDiff = getMonthDiff(outboundMonth, inboundMonth);

    for (let i = 0; i < grid.length; i += 1) {
      // For adjacent months (monthDiff > 0), check all outbound days
      // For same month (monthDiff === 0), only check valid combinations (j <= i)
      const maxJ = monthDiff > 0 ? grid[i].length : i + 1;

      for (let j = 0; j < maxJ; j += 1) {
        total += 1;
        if (grid[i][j] && getTotalPrice(grid[i][j]) !== null) {
          withPrices += 1;
        }
      }
    }

    // whole number percentage to send to slipstream as a number
    const completeness = total > 0 ? withPrices / total : 0;
    return Math.round(completeness * 100);
  },
);

// Checks if the given outbound/inbound index pair matches the requested duration
const isValidDurationMatch = ({
  duration,
  inboundIndex,
  inboundMonth,
  outboundDayOfWeek,
  outboundIndex,
  outboundMonth,
}: {
  duration: DateSelectionState['tripDuration'];
  outboundIndex: number;
  inboundIndex: number;
  outboundDayOfWeek: number | undefined;
  outboundMonth: Date;
  inboundMonth: Date;
}): boolean => {
  const outboundDate = parseISO(
    `${outboundMonth.getFullYear()}-${String(outboundMonth.getMonth() + 1).padStart(2, '0')}-${String(outboundIndex + 1).padStart(2, '0')}`,
  );

  const inboundDate = parseISO(
    `${inboundMonth.getFullYear()}-${String(inboundMonth.getMonth() + 1).padStart(2, '0')}-${String(inboundIndex + 1).padStart(2, '0')}`,
  );

  const dayDiff = differenceInDays(inboundDate, outboundDate);

  const actualDuration = dayDiff;

  // Fall back to calculating day of week from the date when the grid entry
  // doesn't carry the attribute (e.g. control variant of feature flag).
  const resolvedDayOfWeek = outboundDayOfWeek ?? outboundDate.getDay();

  if (duration === 'weekend') {
    // Weekend = Friday (5) to Sunday (0), duration should be 2 days
    return resolvedDayOfWeek === 5 && actualDuration === 2;
  }
  if (duration === 'longWeekend') {
    // Long weekend = Thursday (4) to Sunday (0) OR Friday (5) to Monday (1), duration should be 3 days
    return (
      (resolvedDayOfWeek === 4 || resolvedDayOfWeek === 5) &&
      actualDuration === 3
    );
  }

  // Numeric duration
  return actualDuration === duration;
};

// Validates if a combination should be included based on filters and price availability
const shouldIncludeCombination = (
  gridEntry: MonthDataGridElement,
  inboundIndex: number,
  outboundIndex: number,
  isReturnTrip: boolean,
  preferDirects: boolean,
  outboundMonth: Date,
  inboundMonth: Date,
): boolean => {
  const currentMonthsInfo = {
    outbound: outboundMonth,
    inbound: inboundMonth || outboundMonth,
    outboundThisMonth: isThisMonth(outboundMonth),
    sameMonth: outboundMonth === inboundMonth,
  };

  const passesFilters = gridEntryPassesFilters(
    gridEntry,
    inboundIndex,
    outboundIndex,
    currentMonthsInfo,
    isReturnTrip,
    preferDirects,
  );

  return passesFilters && getTotalPrice(gridEntry) !== null;
};

export const makeGetCheapestCombination = (): ((
  state: State,
  tripDuration?: DateSelectionState['tripDuration'],
) => CheapestCombination | null) =>
  createSelector(
    [
      (state: State, tripDuration?: DateSelectionState['tripDuration']) =>
        tripDuration ?? getTripDuration(state),
      getMonthData,
      isReturn,
      getPreferDirects,
      getSelectedOutboundMonth,
      getSelectedInboundMonth,
    ],
    (
      duration,
      grid,
      isReturnTrip,
      preferDirects,
      outboundMonth,
      inboundMonth,
    ): CheapestCombination | null => {
      if (!duration) return null;
      if (!isReturnTrip) return null;
      if (!inboundMonth) return null; // needed as isReturnTrip is decoupled from inboundMonth presence
      if (!grid || grid.length === 0) return null;

      // Check if months are same or adjacent
      const monthDiff = getMonthDiff(outboundMonth, inboundMonth);
      if (monthDiff > 1) return null; // Only support same month or adjacent

      const combinations: CheapestCombination[] = [];

      // Iterate through grid to find matching combinations
      for (
        let inboundIndex = 0;
        inboundIndex < grid.length;
        inboundIndex += 1
      ) {
        for (
          let outboundIndex = 0;
          outboundIndex < grid[inboundIndex].length;
          outboundIndex += 1
        ) {
          const gridEntry = grid[inboundIndex][outboundIndex];

          // Check if duration matches
          if (
            isValidDurationMatch({
              duration,
              outboundIndex,
              inboundIndex,
              outboundDayOfWeek: gridEntry.outboundDayOfWeek,
              outboundMonth,
              inboundMonth,
            })
          ) {
            // Validate filters and check price availability
            if (
              shouldIncludeCombination(
                gridEntry,
                inboundIndex,
                outboundIndex,
                isReturnTrip,
                preferDirects,
                outboundMonth,
                inboundMonth,
              )
            ) {
              // Add valid combination
              const price = preferDirects
                ? getDirectTotalPrice(gridEntry)
                : getTotalPrice(gridEntry);
              if (price !== null) {
                combinations.push({
                  outboundIndex,
                  inboundIndex,
                  price,
                  isDirect: gridEntry.Direct !== undefined,
                });
              }
            }
          }
        }
      }

      // Sort combinations by price (cheapest first), with direct flights preferred when prices are equal
      combinations.sort((a, b) => {
        if (a.price !== b.price) return a.price - b.price;
        if (a.isDirect === b.isDirect) return 0;
        return a.isDirect ? -1 : 1; // Prefer direct when prices equal
      });

      // Return only the cheapest combination, or null if no combinations found
      return combinations.length > 0 ? combinations[0] : null;
    },
  );

export const getComboExists = createSelector(
  getCheapestCombinations,
  (combosByDuration) =>
    !!(
      combosByDuration?.weekend ||
      combosByDuration?.longWeekend ||
      combosByDuration?.[7] ||
      combosByDuration?.[14]
    ),
);

// Derives the chokepoint condition directly from source data (month grid,
// isReturn, etc.) rather than from the dispatched cheapestCombinations store
// value. This avoids an extra render-cycle gap where the component could
// unmount before the chokepoint fires.
const getWeekendCombo = makeGetCheapestCombination();
const getLongWeekendCombo = makeGetCheapestCombination();
const getWeekCombo = makeGetCheapestCombination();
const getTwoWeekCombo = makeGetCheapestCombination();

export const shouldFireAutoselectChokePoint = createSelector(
  (state: State) => getWeekendCombo(state, 'weekend'),
  (state: State) => getLongWeekendCombo(state, 'longWeekend'),
  (state: State) => getWeekCombo(state, 7),
  (state: State) => getTwoWeekCombo(state, 14),
  (weekend, longWeekend, week, twoWeek) =>
    !!(weekend || longWeekend || week || twoWeek),
);
